import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import { Toaster } from "sonner";
import LeviDiagnosticsBadge from "@/components/dev/LeviDiagnosticsBadge";

import Landing from "@/pages/Landing";
import FeaturesProof from "@/pages/design-proof/FeaturesProof";
import HowItWorks from "@/pages/HowItWorks";
import Pricing from "@/pages/Pricing";
import OurStory from "@/pages/OurStory";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import PricingProfile from "@/pages/PricingProfile";
import PricingProfileWizard from "@/pages/PricingProfileWizard";
import MaterialPriceBook from "@/pages/MaterialPriceBook";
import CompanyProfile from "@/pages/CompanyProfile";
import DocumentDefaults from "@/pages/DocumentDefaults";
import CloseoutDefaults from "@/pages/CloseoutDefaults";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import CrewPacket from "@/pages/CrewPacket";
import MaterialTakeoff from "@/pages/MaterialTakeoff";
import Profitability from "@/pages/Profitability";
import { ChangeOrdersList, ChangeOrderDetail, ChangeOrderPreview } from "@/pages/ChangeOrders";
import Schedule from "@/pages/Schedule";
import NewEstimate from "@/pages/NewEstimate";
import LeviEstimate from "@/pages/LeviEstimate";
import { EstimatesList, EstimateDetail } from "@/pages/Estimates";
import { ProposalsList, ProposalDetail } from "@/pages/Proposals";
import { ContractsList, ContractDetail } from "@/pages/Contracts";
import { InvoicesList, InvoiceDetail } from "@/pages/Invoices";
import CustomerUpdates from "@/pages/CustomerUpdates";
import { WorkOrdersList, WorkOrderDetail } from "@/pages/WorkOrders";
import { CloseoutPacketsList, CloseoutPacketDetail } from "@/pages/CloseoutPackets";
import Account from "@/pages/Account";
import TeamMembers from "@/pages/TeamMembers";
import BusinessTexting from "@/pages/BusinessTexting";
import NoAccess from "@/pages/NoAccess";
import Admin from "@/pages/Admin";
import MeetLevi from "@/pages/MeetLevi";
import SettingsLevi from "@/pages/SettingsLevi";
import CompanySettings from "@/pages/CompanySettings";
import QuickStart from "@/pages/QuickStart";
import Setup, { QuickStartRedirect, PricingWizardRedirect } from "@/pages/Setup";
import SetUpYourBusiness from "@/pages/SetUpYourBusiness";
import CustomersList, { CustomerDetail, CustomerNew } from "@/pages/Customers";
import JobDetail from "@/pages/JobDetail";
import EmailOutbox from "@/pages/EmailOutbox";
import SupportRoute from "@/components/support/SupportRoute";
import SupportSearch from "@/pages/support/SupportSearch";
import SupportAccountSummary from "@/pages/support/SupportAccountSummary";
import { PrivacyPolicy, TermsOfUse, DataSecurity } from "@/pages/Legal";
import Support from "@/pages/Support";
import { PublicProposal, PublicContract, PublicCloseoutPacket, PublicChangeOrder, PublicInvoice } from "@/pages/PublicDocs";
import VisualIntake from "@/pages/VisualIntake";
import FieldEvidence from "@/pages/FieldEvidence";
import ScheduleDemo from "@/pages/ScheduleDemo";
import QaLogin from "@/pages/QaLogin";
import QaScopeRoute from "@/components/QaScopeRoute";
import QaFindings from "@/pages/QaFindings";
import AdminQaFindings from "@/pages/AdminQaFindings";
import ErrorBoundary from "@/components/ErrorBoundary";
import PaywallRoute from "@/components/PaywallRoute";
import HelpAndSupport from "@/pages/HelpAndSupport";

const Protected = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--jc-text-soft)]">
        Loading JobClose AI...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return (
    <ErrorBoundary>
      <Outlet />
    </ErrorBoundary>
  );
};

const AdminGuard = () => {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/app" replace />;
  return <Outlet />;
};

// Slice 2D1 · Bridge auth's `user` into LangProvider so the language
// preference persisted on the user record hydrates the interface.
const LangBridge = ({ children }) => {
  const { user } = useAuth();
  return <LangProvider user={user}>{children}</LangProvider>;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LangBridge>
            <Toaster position="top-right" richColors closeButton />
            <LeviDiagnosticsBadge />
            <Routes>
              <Route path="/" element={<Landing />} />
              {/* DESIGN PROOF · PO Features concept translation. Not in
                  navigation. Not the requested-out-of-scope /features
                  route. Temporary; remove after PO review. */}
              <Route path="/design-proof/features" element={<FeaturesProof />} />
              {/* Canonical public route alias for the authoritative
                  Features experience. Renders the same FeaturesProof
                  component — no duplicated page, no content change. */}
              <Route path="/features" element={<FeaturesProof />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/our-story" element={<OurStory />} />
              <Route path="/schedule-a-demo" element={<ScheduleDemo />} />
              <Route path="/home-preview" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              {/* PO 2026-02 · Product Owner QA access reset. Clears
                  ONLY the JobClose Preview auth session and redirects
                  to /login. No new auth system, no production impact. */}
              <Route path="/qa-login" element={<QaLogin />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/data-security" element={<DataSecurity />} />
              <Route path="/support" element={<Support />} />
              <Route path="/p/:token" element={<PublicProposal />} />
              <Route path="/c/:token" element={<PublicContract />} />
              <Route path="/co/:token" element={<PublicCloseoutPacket />} />
              <Route path="/x/:token" element={<PublicChangeOrder />} />
              <Route path="/i/:token" element={<PublicInvoice />} />
              {/* PO 2026-02 · Remote Customer Visual Intake V1A — public no-auth mobile capture. */}
              <Route path="/visual-intake/:token" element={<VisualIntake />} />
              {/* PO 2026-02 · Internal Field Team Project Evidence V1 — public no-auth mobile capture. */}
              <Route path="/field-evidence/:token" element={<FieldEvidence />} />
              <Route path="/no-access" element={<NoAccess />} />

              <Route element={<Protected />}>
                {/* PO 2026-02 · Trial Paywall V1 — allowlisted routes
                    (account, support, legal, QA scope) render OUTSIDE
                    the paywall guard so expired-trial contractors can
                    still reach billing / support / legal. */}
                <Route path="/app/account" element={<Account />} />
                {/* PO 2026-02 · Support Diagnostics V1 — authenticated
                    contractor Help & Support surface. Rendered outside
                    <PaywallRoute> so an expired-trial contractor can
                    still send a diagnostic report to Support. */}
                <Route path="/app/help" element={<HelpAndSupport />} />
                <Route path="/app/help/troubleshooting" element={<HelpAndSupport />} />
                <Route element={<SupportRoute />}>
                  <Route path="/app/support" element={<SupportSearch />} />
                  <Route path="/app/support/accounts/:uid" element={<SupportAccountSummary />} />
                </Route>
                {/* PO 2026-02 · Mohit QA capability-scoped surface —
                    allowlisted from the paywall since QA identities
                    carry the qa_test_account entitlement. */}
                <Route element={<QaScopeRoute />}>
                  <Route path="/app/qa" element={<QaFindings />} />
                  <Route path="/app/qa/findings" element={<QaFindings />} />
                </Route>
                {/* Every other /app/* route is gated by <PaywallRoute>.
                    When entitlement === "expired", the guard renders
                    TrialExpiredScreen instead of the requested page. */}
                <Route element={<PaywallRoute />}>
                <Route path="/app" element={<Dashboard />} />
                <Route path="/app/customers" element={<CustomersList />} />
                <Route path="/app/customers/new" element={<CustomerNew />} />
                <Route path="/app/customers/:cid" element={<CustomerDetail />} />
                <Route path="/app/jobs/:jid" element={<JobDetail />} />
                <Route path="/app/setup" element={<SetUpYourBusiness />} />
                <Route path="/app/setup/legacy" element={<Setup />} />
                <Route path="/app/pricing-profile" element={<PricingProfile />} />
                <Route path="/app/pricing-profile/wizard" element={<PricingWizardRedirect />} />
                {/* Legacy wizard component kept alive via query-param escape hatch
                    so power users / white-glove flows can still open it if needed. */}
                <Route path="/app/pricing-profile/wizard-legacy" element={<PricingProfileWizard />} />
                <Route path="/app/pricing-profile/materials" element={<MaterialPriceBook />} />
                <Route path="/app/company-profile" element={<CompanyProfile />} />
                <Route path="/app/setup/document-defaults" element={<DocumentDefaults />} />
                <Route path="/app/setup/closeout-defaults" element={<CloseoutDefaults />} />
                <Route path="/app/projects" element={<Projects />} />
                <Route path="/app/schedule" element={<Schedule />} />
                <Route path="/app/projects/:pid" element={<ProjectDetail />} />
                <Route path="/app/projects/:pid/crew-packet" element={<CrewPacket />} />
                <Route path="/app/projects/:pid/material-takeoff" element={<MaterialTakeoff />} />
                <Route path="/app/projects/:pid/profitability" element={<Profitability />} />
                <Route path="/app/projects/:pid/change-orders" element={<ChangeOrdersList />} />
                <Route path="/app/projects/:pid/change-orders/:coid" element={<ChangeOrderDetail />} />
                <Route path="/app/projects/:pid/change-orders/:coid/preview" element={<ChangeOrderPreview />} />
                <Route path="/app/estimates" element={<EstimatesList />} />
                <Route path="/app/estimates/new" element={<NewEstimate />} />
                <Route path="/app/estimates/new/levi" element={<LeviEstimate />} />
                <Route path="/app/estimates/:eid" element={<EstimateDetail />} />
                <Route path="/app/proposals" element={<ProposalsList />} />
                <Route path="/app/proposals/:pid" element={<ProposalDetail />} />
                <Route path="/app/contracts" element={<ContractsList />} />
                <Route path="/app/contracts/:cid" element={<ContractDetail />} />
                <Route path="/app/invoices" element={<InvoicesList />} />
                <Route path="/app/invoices/:iid" element={<InvoiceDetail />} />
                <Route path="/app/customer-updates" element={<CustomerUpdates />} />
                <Route path="/app/work-orders" element={<WorkOrdersList />} />
                <Route path="/app/work-orders/:wid" element={<WorkOrderDetail />} />
                <Route path="/app/closeout" element={<CloseoutPacketsList />} />
                <Route path="/app/closeout/:id" element={<CloseoutPacketDetail />} />
                <Route path="/app/team" element={<TeamMembers />} />
                <Route path="/app/settings/business-texting" element={<BusinessTexting />} />
                <Route path="/app/meet-levi" element={<MeetLevi />} />                <Route path="/app/settings/levi" element={<SettingsLevi />} />                <Route path="/app/settings" element={<CompanySettings />} />                <Route path="/app/quick-start" element={<QuickStartRedirect />} />                <Route path="/app/quick-start/legacy" element={<QuickStart />} />                <Route element={<AdminGuard />}>
                  <Route path="/app/admin" element={<Admin />} />
                  <Route path="/app/admin/email-outbox" element={<EmailOutbox />} />
                  {/* PO 2026-02 · Mohit QA Reporting V1 (Preview-only) */}
                  <Route path="/app/admin/qa-findings" element={<AdminQaFindings />} />
                </Route>
                </Route>
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </LangBridge>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
